/**
 * Tournament simulation engine using fm-engine-ts in the browser.
 * Replaces the Rust API backend for all tournament operations.
 *
 * Public API:
 *   loadWorldCupData()     → manifest
 *   createInitialRun()     → run state
 *   stepRun()              → simulate one match (audience mode)
 *   advanceRun()           → simulate entire matchday (coach mode)
 *   buildTacticFromPayload → convert UI payload to engine Tactic
 */

import { simulateMatch, Xoshiro128 } from '../../engine-ts/dist/index.js';

// ============================================================
// Constants
// ============================================================

const WORLD_CUP_BASE = '/wc26';

const MENTALITY_MAP = [
  'veryDefensive', 'defensive', 'cautious', 'balanced',
  'positive', 'attacking', 'veryAttacking',
];

const FOCUS_MAP = ['left', 'mixed', 'center'];

const GK_DIST_MAP = ['short', 'fullBacks', 'centerBacks', 'targetForward', 'mixed'];

const MENTALITY_RISK = [0, 1, 2, 3, 4, 5, 6];

const TEAM_ODDS = {
  spain: 4.5, england: 5.5, france: 6, brazil: 8, argentina: 8,
  portugal: 12, germany: 12, netherlands: 20,
  norway: 25, belgium: 33, united_states: 40, switzerland: 40,
  colombia: 50, morocco: 50, japan: 66,
  mexico: 80, uruguay: 80, croatia: 80, senegal: 80, ecuador: 80,
  turkiye: 100, egypt: 100, ghana: 100, ivory_coast: 100,
  algeria: 100, iran: 125, saudi_arabia: 125, iraq: 150,
  jordan: 150, qatar: 150, uzbekistan: 150, australia: 150,
  paraguay: 150, tunisia: 175, canada: 175, czechia: 175,
  south_korea: 175, scotland: 200, austria: 200, sweden: 200,
  bosnia_herzegovina: 200, panama: 250, cape_verde: 250,
  haiti: 300, new_zealand: 300, curacao: 300, dr_congo: 300,
  italy: 12, china: 300, indonesia: 2000, united_arab_emirates: 1500, poland: 150,
};

const DEFAULT_CA = 140;

const TEAM_STRENGTH_MULTIPLIERS = {
  united_states: 1.10,
  mexico: 1.10,
  canada: 1.10,
  japan: 1.10,
  south_korea: 1.10,
  morocco: 1.20,
};

const KNOCKOUT_SLOTS_BY_ROUND = [
  // ==============================
  // Round of 32 (June 28 – July 3)
  // ==============================
  { id: 73, stage: 'Round of 32', date: '2026-06-28 12:00', venue: 'SoFi Stadium', home: ['rank', 'A', 2], away: ['rank', 'B', 2] },
  { id: 74, stage: 'Round of 32', date: '2026-06-29 12:00', venue: 'NRG Stadium', home: ['rank', 'C', 1], away: ['rank', 'F', 2] },
  { id: 75, stage: 'Round of 32', date: '2026-06-29 16:30', venue: 'Gillette Stadium', home: ['rank', 'E', 1], away: ['third', ['A', 'B', 'C', 'D', 'F']] },
  { id: 76, stage: 'Round of 32', date: '2026-06-29 19:00', venue: 'Estadio BBVA', home: ['rank', 'F', 1], away: ['rank', 'C', 2] },
  { id: 77, stage: 'Round of 32', date: '2026-06-30 12:00', venue: 'AT&T Stadium', home: ['rank', 'E', 2], away: ['rank', 'I', 2] },
  { id: 78, stage: 'Round of 32', date: '2026-06-30 17:00', venue: 'MetLife Stadium', home: ['rank', 'I', 1], away: ['third', ['C', 'D', 'F', 'G', 'H']] },
  { id: 79, stage: 'Round of 32', date: '2026-06-30 19:00', venue: 'Estadio Azteca', home: ['rank', 'A', 1], away: ['third', ['C', 'E', 'F', 'H', 'I']] },
  { id: 80, stage: 'Round of 32', date: '2026-07-01 12:00', venue: 'Mercedes-Benz Stadium', home: ['rank', 'L', 1], away: ['third', ['E', 'H', 'I', 'J', 'K']] },
  { id: 81, stage: 'Round of 32', date: '2026-07-01 13:00', venue: 'Lumen Field', home: ['rank', 'G', 1], away: ['third', ['A', 'E', 'H', 'I', 'J']] },
  { id: 82, stage: 'Round of 32', date: '2026-07-01 17:00', venue: "Levi's Stadium", home: ['rank', 'D', 1], away: ['third', ['B', 'E', 'F', 'I', 'J']] },
  { id: 83, stage: 'Round of 32', date: '2026-07-02 12:00', venue: 'SoFi Stadium', home: ['rank', 'H', 1], away: ['rank', 'J', 2] },
  { id: 84, stage: 'Round of 32', date: '2026-07-02 19:00', venue: 'BMO Field', home: ['rank', 'K', 2], away: ['rank', 'L', 2] },
  { id: 85, stage: 'Round of 32', date: '2026-07-02 20:00', venue: 'BC Place', home: ['rank', 'B', 1], away: ['third', ['E', 'F', 'G', 'I', 'J']] },
  { id: 86, stage: 'Round of 32', date: '2026-07-03 13:00', venue: 'AT&T Stadium', home: ['rank', 'D', 2], away: ['rank', 'G', 2] },
  { id: 87, stage: 'Round of 32', date: '2026-07-03 18:00', venue: 'Hard Rock Stadium', home: ['rank', 'J', 1], away: ['rank', 'H', 2] },
  { id: 88, stage: 'Round of 32', date: '2026-07-03 20:30', venue: 'Arrowhead Stadium', home: ['rank', 'K', 1], away: ['third', ['D', 'E', 'I', 'J', 'L']] },
  // ==============================
  // Round of 16 (July 4–7)
  // ==============================
  { id: 89, stage: 'Round of 16', date: '2026-07-04 12:00', venue: 'NRG Stadium', home: ['winner', 73], away: ['winner', 75] },
  { id: 90, stage: 'Round of 16', date: '2026-07-04 17:00', venue: 'Lincoln Financial Field', home: ['winner', 74], away: ['winner', 77] },
  { id: 91, stage: 'Round of 16', date: '2026-07-05 16:00', venue: 'MetLife Stadium', home: ['winner', 76], away: ['winner', 78] },
  { id: 92, stage: 'Round of 16', date: '2026-07-05 18:00', venue: 'Estadio Azteca', home: ['winner', 79], away: ['winner', 80] },
  { id: 93, stage: 'Round of 16', date: '2026-07-06 14:00', venue: 'AT&T Stadium', home: ['winner', 83], away: ['winner', 84] },
  { id: 94, stage: 'Round of 16', date: '2026-07-06 17:00', venue: 'Lumen Field', home: ['winner', 81], away: ['winner', 82] },
  { id: 95, stage: 'Round of 16', date: '2026-07-07 12:00', venue: 'Mercedes-Benz Stadium', home: ['winner', 86], away: ['winner', 88] },
  { id: 96, stage: 'Round of 16', date: '2026-07-07 13:00', venue: 'BC Place', home: ['winner', 85], away: ['winner', 87] },
  // ==============================
  // Quarter-finals (July 9–11)
  // ==============================
  { id: 97, stage: 'Quarter-final', date: '2026-07-09 16:00', venue: 'Gillette Stadium', home: ['winner', 89], away: ['winner', 90] },
  { id: 98, stage: 'Quarter-final', date: '2026-07-10 12:00', venue: 'SoFi Stadium', home: ['winner', 93], away: ['winner', 94] },
  { id: 99, stage: 'Quarter-final', date: '2026-07-11 17:00', venue: 'Hard Rock Stadium', home: ['winner', 91], away: ['winner', 92] },
  { id: 100, stage: 'Quarter-final', date: '2026-07-11 20:00', venue: 'Arrowhead Stadium', home: ['winner', 95], away: ['winner', 96] },
  // ==============================
  // Semi-finals (July 14–15)
  // ==============================
  { id: 101, stage: 'Semi-final', date: '2026-07-14 14:00', venue: 'AT&T Stadium', home: ['winner', 97], away: ['winner', 98] },
  { id: 102, stage: 'Semi-final', date: '2026-07-15 15:00', venue: 'Mercedes-Benz Stadium', home: ['winner', 99], away: ['winner', 100] },
  // ==============================
  // Third Place & Final
  // ==============================
  { id: 103, stage: 'Third Place', date: '2026-07-18 17:00', venue: 'Hard Rock Stadium', home: ['loser', 101], away: ['loser', 102] },
  { id: 104, stage: 'Final', date: '2026-07-19 15:00', venue: 'MetLife Stadium', home: ['winner', 101], away: ['winner', 102] },
];

// ============================================================
// Helpers
// ============================================================

function hashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash) || 1;
}

function estimateCA(teamId) {
  const odds = TEAM_ODDS[teamId];
  const base = odds ? 185 - Math.log2(odds) * 9 : DEFAULT_CA;
  const multiplier = TEAM_STRENGTH_MULTIPLIERS[teamId] || 1;
  return Math.max(100, Math.min(200, Math.round(base * multiplier)));
}

export function fixtureDayKey(fixture) {
  if (!fixture) return '';
  return fixture.date ? String(fixture.date).slice(0, 10) : '';
}

// ============================================================
// Manifest Loading
// ============================================================

export async function loadWorldCupData() {
  const [teamsJson, groupsJson] = await Promise.all([
    fetch(`${WORLD_CUP_BASE}/teams.json`).then(r => r.json()),
    fetch(`${WORLD_CUP_BASE}/groups.json`).then(r => r.json()),
  ]);

  const allTeamIds = teamsJson.teams;
  const groupMap = new Map();
  for (const g of groupsJson.groups) {
    for (const tid of g.teams) groupMap.set(tid, g.id);
  }

  const rosterResults = await Promise.allSettled(
    allTeamIds.map(async (teamId) => {
      const resp = await fetch(`${WORLD_CUP_BASE}/rosters/${teamId}.json`);
      if (!resp.ok) return null;
      return resp.json();
    })
  );

  const rosterMap = new Map(
    rosterResults
      .map((r) => (r.status === 'fulfilled' ? r.value : null))
      .filter(Boolean)
      .map((r) => [r.teamId, r])
  );

  const teams = [];
  for (const teamId of allTeamIds) {
    const roster = rosterMap.get(teamId);
    const groupId = groupMap.get(teamId) || 'A';

    if (roster) {
      const players = roster.players.map((p, index) => ({
        id: p.id,
        name: p.name,
        number: p.number || String(index + 1),
        club: p.club || '',
        age: p.age || 26,
        position: p.position,
        rating: p.ability,
        currentAbility: p.ability,
        stamina: 90,
        injured: false,
        names: p.names || { en: p.name },
      }));
      teams.push({
        id: teamId,
        name: roster.teamName?.en || teamId,
        group: groupId,
        strength: estimateCA(teamId),
        players,
        defaultStartingXi: roster.startingXi || players.slice(0, 11).map((p) => p.id),
        defaultTactic: buildRosterTactic(roster.defaultTactic || {}),
        defaultTacticSimple: roster.defaultTactic || {},
        coach: roster.coach ? { name: roster.coach.name?.en || '' } : undefined,
      });
    } else {
      const ca = estimateCA(teamId);
      const players = generateSyntheticPlayers(teamId, ca);
      teams.push({
        id: teamId,
        name: teamNameEnglish(teamId),
        group: groupId,
        strength: ca,
        players,
        defaultStartingXi: players.slice(0, 11).map((p) => p.id),
        defaultTactic: defaultNeutralTactic(),
        defaultTacticSimple: {},
      });
    }
  }

  // Also load hidden team rosters (italy, china) for the hidden teams feature
  const hiddenTeamIds = ['italy', 'china', 'indonesia', 'united_arab_emirates', 'poland'];
  const hiddenRosterResults = await Promise.allSettled(
    hiddenTeamIds.map(async (teamId) => {
      const resp = await fetch(`${WORLD_CUP_BASE}/rosters/${teamId}.json`);
      if (!resp.ok) return null;
      return resp.json();
    })
  );

  const hiddenTeamData = {};
  for (const result of hiddenRosterResults) {
    if (result.status !== 'fulfilled' || !result.value) continue;
    const roster = result.value;
    const players = roster.players.map((p, index) => ({
      id: p.id,
      name: p.name,
      number: p.number || String(index + 1),
      club: p.club || '',
      age: p.age || 26,
      position: p.position,
      rating: p.ability,
      currentAbility: p.ability,
      stamina: 90,
      injured: false,
      names: p.names || { en: p.name },
    }));
    hiddenTeamData[roster.teamId] = {
      id: roster.teamId,
      name: roster.teamName?.en || roster.teamId,
      group: '(hidden)',
      strength: estimateCA(roster.teamId),
      players,
      defaultStartingXi: roster.startingXi || players.slice(0, 11).map((p) => p.id),
      defaultTactic: buildRosterTactic(roster.defaultTactic || {}),
      defaultTacticSimple: roster.defaultTactic || {},
      coach: roster.coach ? { name: roster.coach.name?.en || '' } : undefined,
    };
  }

  const fixtures = generateGroupFixtures(groupsJson.groups);
  fixtures.sort((a, b) => a.date.localeCompare(b.date));

  return { teams, groups: groupsJson.groups, fixtures, hiddenTeamData };
}

// ============================================================
// Team name / Synthetic generation
// ============================================================

function teamNameEnglish(teamId) {
  const map = {
    algeria: 'Algeria', australia: 'Australia', brazil: 'Brazil',
    canada: 'Canada', colombia: 'Colombia', czechia: 'Czechia',
    ecuador: 'Ecuador', egypt: 'Egypt', ghana: 'Ghana',
    iran: 'Iran', iraq: 'Iraq', jordan: 'Jordan',
    mexico: 'Mexico', paraguay: 'Paraguay', qatar: 'Qatar',
    saudi_arabia: 'Saudi Arabia', senegal: 'Senegal',
    sweden: 'Sweden', poland: 'Poland',
    turkiye: 'Türkiye', uruguay: 'Uruguay', uzbekistan: 'Uzbekistan',
    indonesia: 'Indonesia', united_arab_emirates: 'United Arab Emirates',
  };
  return map[teamId] || teamId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function generateSyntheticPlayers(teamId, averageCA) {
  const posSlots = [
    'GK', 'GK', 'GK',
    'RB', 'CB', 'CB', 'CB', 'CB', 'LB',
    'DM', 'DM', 'CM', 'CM', 'CM', 'AM', 'AM',
    'RW', 'LW', 'ST', 'ST', 'ST', 'RW', 'LW',
  ];

  const rng = new Xoshiro128(hashCode(teamId));
  const firstNames = ['James','John','Robert','Michael','David','William','Richard','Joseph','Thomas',
    'Chris','Daniel','Paul','Mark','Donald','George','Kenneth','Steven','Edward','Brian','Ronald',
    'Anthony','Kevin','Jason'];
  const lastNames = ['Smith','Johnson','Williams','Brown','Jones','Garcia','Miller','Davis','Martinez',
    'Taylor','Anderson','Thomas','Jackson','White','Harris','Clark','Lewis','Robinson','Walker','Young',
    'Allen','King','Wright'];

  return posSlots.map((pos, i) => {
    const offset = Math.round(rng.genRange(-15, 15));
    const ability = Math.max(60, Math.min(200, averageCA + offset));
    const name = `${firstNames[i]} ${lastNames[i % lastNames.length]}`;
    const id = `${teamId}_p${String(i + 1).padStart(2, '0')}`;
    return {
      id, name, number: String(i + 1), club: '', age: 24 + Math.floor(rng.genRange(0, 12)),
      position: pos, rating: ability, currentAbility: ability,
      stamina: 90, injured: false,
    };
  });
}

// ============================================================
// Tactics
// ============================================================

function buildRosterTactic(simple) {
  const mentality = simple.mentality || 'balanced';
  const mentalityIdx = MENTALITY_MAP.indexOf(mentality);
  const level = mentalityIdx >= 0 ? mentalityIdx : 3;

  return {
    base: {
      formation: simple.formation || '4-3-3',
      mentality,
      tempo: simple.passingDirectness !== undefined ? 4 - simple.passingDirectness : 2,
      risk: MENTALITY_RISK[level] ?? 3,
      attackingWidth: 2, defensiveWidth: 2, creativeFreedom: 2, timeManagement: 2,
    },
    inPossession: {
      passingDirectness: simple.passingDirectness ?? 2,
      passingTempo: simple.passingDirectness ?? 2,
      shortPassing: simple.shortPassing ?? 2,
      throughBalls: 2, diagonalSwitches: 2, playOutOfDefence: 2,
      overlapLeft: 2, overlapRight: 2, underlapLeft: 1, underlapRight: 1,
      crossingFrequency: 2, crossingType: 'mixed',
      shootOnSight: 2, workBallIntoBox: 2, boxPresence: 2,
      attackFocus: simple.attackFocus || 'mixed',
    },
    transition: {
      whenPossessionLost: 'regroup', whenPossessionWon: 'counter',
      goalkeeperDistribution: 'mixed', quickSwitches: 2, targetForwardUsage: 2,
    },
    outOfPossession: {
      pressingIntensity: simple.pressingIntensity ?? 2,
      pressingTriggers: 1, defensiveLine: simple.defensiveLine ?? 2,
      lineOfEngagement: 1, offsideTrap: 1, markingStyle: 'zonal',
      tacklingIntensity: 1, compactness: 1, preventShortGkDistribution: 1,
    },
    setPieces: {
      attackingCorners: 2, defensiveCorners: 2, attackingFreeKicks: 2,
      defensiveFreeKicks: 2, penalties: [], longThrows: 1,
      aerialTargets: [], secondBallShape: 2,
    },
    playerInstructions: [],
  };
}

function defaultNeutralTactic() {
  return buildRosterTactic({});
}

/** Convert web UI tactic payload → engine Tactic */
export function buildTacticFromPayload(payload) {
  const mentalityIdx = payload.mentalityLevel ?? 3;
  const passLevel = payload.passingLevel ?? 2;

  return {
    base: {
      formation: payload.formation || '4-2-3-1A',
      mentality: MENTALITY_MAP[mentalityIdx] || 'balanced',
      tempo: payload.tempoLevel ?? 2,
      risk: MENTALITY_RISK[mentalityIdx] ?? 3,
      attackingWidth: payload.widthLevel ?? 2,
      defensiveWidth: 2,
      creativeFreedom: 2,
      timeManagement: payload.possessionLevel ?? 2,
    },
    inPossession: {
      passingDirectness: Math.max(0, 4 - passLevel),
      passingTempo: payload.tempoLevel ?? 2,
      shortPassing: Math.max(1, passLevel),
      throughBalls: 2, diagonalSwitches: 2,
      playOutOfDefence: payload.transitionLevel ?? 1,
      overlapLeft: 2, overlapRight: 2, underlapLeft: 1, underlapRight: 1,
      crossingFrequency: 2, crossingType: 'mixed',
      shootOnSight: 2, workBallIntoBox: 2, boxPresence: 2,
      attackFocus: FOCUS_MAP[payload.attackRouteLevel ?? 1] || 'mixed',
    },
    transition: {
      whenPossessionLost: 'regroup', whenPossessionWon: 'counter',
      goalkeeperDistribution: GK_DIST_MAP[payload.transitionLevel ?? 1] || 'mixed',
      quickSwitches: 2, targetForwardUsage: 2,
    },
    outOfPossession: {
      pressingIntensity: payload.pressingLevel ?? 1,
      pressingTriggers: payload.pressingLevel ?? 1,
      defensiveLine: payload.defensiveLineLevel ?? 1,
      lineOfEngagement: payload.defensiveLineLevel ?? 1,
      offsideTrap: 0, markingStyle: 'zonal',
      tacklingIntensity: 1,
      compactness: 2, preventShortGkDistribution: 0,
    },
    setPieces: {
      attackingCorners: 2, defensiveCorners: 2, attackingFreeKicks: 2,
      defensiveFreeKicks: 2, penalties: [], longThrows: 1,
      aerialTargets: [], secondBallShape: 2,
    },
    playerInstructions: [],
  };
}

// ============================================================
// Fixture Generation
// ============================================================

/** Each group's 6 fixtures as [homeTeamIndex, awayTeamIndex, date] */
const GROUP_FIXTURE_DEFS = {
  A: [[0,1,'2026-06-11'],[2,3,'2026-06-11'],[3,1,'2026-06-18'],[0,2,'2026-06-18'],[3,0,'2026-06-24'],[1,2,'2026-06-24']],
  B: [[0,1,'2026-06-12'],[3,2,'2026-06-13'],[2,1,'2026-06-18'],[0,3,'2026-06-18'],[2,0,'2026-06-24'],[1,3,'2026-06-24']],
  C: [[0,1,'2026-06-13'],[3,2,'2026-06-13'],[2,1,'2026-06-19'],[0,3,'2026-06-19'],[2,0,'2026-06-24'],[1,3,'2026-06-24']],
  D: [[0,2,'2026-06-12'],[1,3,'2026-06-14'],[0,1,'2026-06-19'],[3,2,'2026-06-19'],[3,0,'2026-06-25'],[2,1,'2026-06-25']],
  E: [[0,1,'2026-06-14'],[2,3,'2026-06-14'],[0,2,'2026-06-20'],[3,1,'2026-06-20'],[1,2,'2026-06-25'],[3,0,'2026-06-25']],
  F: [[0,1,'2026-06-14'],[3,2,'2026-06-14'],[0,3,'2026-06-20'],[2,1,'2026-06-21'],[1,3,'2026-06-25'],[2,0,'2026-06-25']],
  G: [[0,2,'2026-06-15'],[1,3,'2026-06-15'],[0,1,'2026-06-21'],[3,2,'2026-06-21'],[2,1,'2026-06-26'],[3,0,'2026-06-26']],
  H: [[0,3,'2026-06-15'],[2,1,'2026-06-15'],[0,2,'2026-06-21'],[1,3,'2026-06-21'],[3,2,'2026-06-26'],[1,0,'2026-06-26']],
  I: [[0,1,'2026-06-16'],[3,2,'2026-06-16'],[0,3,'2026-06-22'],[2,1,'2026-06-22'],[2,0,'2026-06-26'],[1,3,'2026-06-26']],
  J: [[0,2,'2026-06-16'],[1,3,'2026-06-17'],[0,1,'2026-06-22'],[3,2,'2026-06-22'],[2,1,'2026-06-27'],[3,0,'2026-06-27']],
  K: [[0,3,'2026-06-17'],[2,1,'2026-06-17'],[0,2,'2026-06-23'],[1,3,'2026-06-23'],[1,0,'2026-06-27'],[3,2,'2026-06-27']],
  L: [[0,1,'2026-06-17'],[3,2,'2026-06-17'],[0,3,'2026-06-23'],[2,1,'2026-06-23'],[2,0,'2026-06-27'],[1,3,'2026-06-27']],
};

export function generateGroupFixtures(groups) {
  const fixtures = [];
  for (const group of groups) {
    const teams = group.teams;
    const defs = GROUP_FIXTURE_DEFS[group.id];
    if (!defs) continue;
    defs.forEach(([homeIdx, awayIdx, date], idx) => {
      const homeTeamId = teams[homeIdx];
      const awayTeamId = teams[awayIdx];
      fixtures.push({
        id: `group-${group.id}-match-${idx + 1}`,
        stage: 'Group Stage',
        group: group.id,
        matchday: Math.floor(idx / 2) + 1,
        date,
        homeTeamId,
        awayTeamId,
        homeTeamName: homeTeamId,
        awayTeamName: awayTeamId,
      });
    });
  }
  return fixtures;
}

// ============================================================
// Run Creation
// ============================================================

export function createInitialRun(manifest, config) {
  const runId = `run-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
  const runSeed = config.seed ?? Math.floor(Math.random() * 2147483646) + 1;
  const team = manifest.teams.find((t) => t.id === config.selectedTeamId) || manifest.teams[0];

  // Derive request defaults from team's defaultTacticSimple when config omits them
  const st = team.defaultTacticSimple || {};
  const mentalityLevelMap = { veryDefensive: 0, defensive: 1, cautious: 2, balanced: 3, positive: 4, attacking: 5, veryAttacking: 6 };
  const defMentality = st.mentality || 'balanced';
  const defFormation = st.formation || '4-3-3';

  const groups = manifest.groups.map((group) => ({
    id: group.id,
    standings: group.teams.map((teamId) => {
      const t = manifest.teams.find((tm) => tm.id === teamId);
      return {
        teamId, teamName: t?.name || teamId,
        rank: 0, played: 0, wins: 0, draws: 0, losses: 0,
        goalsFor: 0, goalsAgainst: 0, goalDifference: 0, points: 0,
      };
    }),
    qualifiers: [],
  }));

  return {
    runId,
    seed: runSeed,
    selectedTeamId: team.id,
    request: {
      formation: config.formation || defFormation,
      mentality: config.mentality || defMentality,
      pressing: config.pressing || (st.pressingIntensity >= 4 ? 'high' : st.pressingIntensity >= 3 ? 'mid' : 'low'),
      passing: config.passing || ((st.passingDirectness || 2) >= 3 ? 'short' : (st.passingDirectness || 2) <= 1 ? 'direct' : 'mixed'),
      attackFocus: config.attackFocus || st.attackFocus || 'mixed',
      widthLevel: config.widthLevel ?? 2,
      tempoLevel: config.tempoLevel ?? 2,
      transitionLevel: config.transitionLevel ?? 1,
      defensiveLineLevel: config.defensiveLineLevel ?? Math.min(4, Math.max(0, (st.defensiveLine || 2) - 1)),
      possessionLevel: config.possessionLevel ?? 2,
      mentalityLevel: config.mentalityLevel ?? (mentalityLevelMap[defMentality] ?? 3),
      pressingLevel: config.pressingLevel ?? (st.pressingIntensity ? Math.min(2, Math.max(0, Math.floor((st.pressingIntensity - 1) / 2))) : 1),
      passingLevel: config.passingLevel ?? Math.min(4, Math.max(0, (st.passingDirectness || 2) - 1)),
      attackRouteLevel: config.attackRouteLevel ?? (st.attackFocus === 'left' ? 0 : st.attackFocus === 'center' ? 2 : 1),
      startingXi: config.startingXi || team.defaultStartingXi,
    },
    matches: [],
    groups,
    knockoutRounds: [
      { name: 'Round of 32', matches: [] },
      { name: 'Round of 16', matches: [] },
      { name: 'Quarter-final', matches: [] },
      { name: 'Semi-final', matches: [] },
      { name: 'Third Place', matches: [] },
      { name: 'Final', matches: [] },
    ],
    nextMatch: manifest.fixtures[0] || null,
    completed: false,
    selectedPath: [],
  };
}

// ============================================================
// Public API: stepRun (audience mode, one match)
// ============================================================

export function stepRun(manifest, run, tacticsPayload = null) {
  const isCoach = !!tacticsPayload;
  return simulateNextMatch(manifest, run, tacticsPayload, isCoach);
}

// ============================================================
// Public API: advanceRun (coach mode, entire matchday)
// ============================================================

export function advanceRun(manifest, run, tacticsPayload) {
  const day = fixtureDayKey(run.nextMatch);
  if (!day) return { run, focusMatch: null, finalPopup: false };

  let focusMatch = null;

  while (run.nextMatch && fixtureDayKey(run.nextMatch) === day && !run.completed) {
    const result = simulateNextMatch(manifest, run, tacticsPayload, true);
    if (result.focusMatch) focusMatch = result.focusMatch;
  }

  return { run, focusMatch, finalPopup: run.completed };
}

// ============================================================
// Core: simulate next match
// ============================================================

let _matchCounter = 0;

function simulateNextMatch(manifest, run, tacticsPayload, isCoachMode) {
  if (run.completed || !run.nextMatch) {
    return { run, focusMatch: null, simulated: false };
  }

  // Refresh knockout projections (fills Round of 32 etc.)
  refreshKnockoutRounds(manifest, run);

  const fixture = run.nextMatch;
  const homeTeamData = manifest.teams.find((t) => t.id === fixture.homeTeamId);
  const awayTeamData = manifest.teams.find((t) => t.id === fixture.awayTeamId);

  if (!homeTeamData || !awayTeamData || fixture.homeTeamId === 'tbd' || fixture.awayTeamId === 'tbd') {
    // Skip unresolvable fixture
    determineNextMatch(manifest, run);
    return { run, focusMatch: null, simulated: false };
  }

  const seed = (run.seed || 1) + hashCode(fixture.id || `${run.matches.length}`);

  // Determine tactics: user's team uses payload tactic in coach mode
  const homeUsesPayload = isCoachMode && tacticsPayload && fixture.homeTeamId === run.selectedTeamId;
  const awayUsesPayload = isCoachMode && tacticsPayload && fixture.awayTeamId === run.selectedTeamId;
  const homeTactic = homeUsesPayload ? buildTacticFromPayload(tacticsPayload) : homeTeamData.defaultTactic;
  const awayTactic = awayUsesPayload ? buildTacticFromPayload(tacticsPayload) : awayTeamData.defaultTactic;

  const homeTeam = buildSimpleTeam(homeTeamData, fixture.homeTeamId, homeTactic);
  const awayTeam = buildSimpleTeam(awayTeamData, fixture.awayTeamId, awayTactic);

  // Coach mode: boost player's team ability by 10% for a tangible win-rate edge
  if (isCoachMode) {
    const boostTeam = homeTeam.id === run.selectedTeamId ? homeTeam
      : awayTeam.id === run.selectedTeamId ? awayTeam : null;
    if (boostTeam) {
      boostTeam.players.forEach((p) => { p.currentAbility = Math.round(p.currentAbility * 1.10); });
    }
  }

  const knockoutDepth = fixture.stage === 'Group Stage' ? 0
    : fixture.stage === 'Round of 32' ? 1
    : fixture.stage === 'Round of 16' || fixture.stage === 'Third Place' ? 2
    : fixture.stage === 'Quarter-final' ? 3
    : fixture.stage === 'Semi-final' ? 4
    : 5; // Final
  let match;
  try {
    const result = simulateMatch(homeTeam, awayTeam, seed, knockoutDepth);
    match = convertMatchResult(result, fixture, homeTeamData, awayTeamData, homeTactic, awayTactic);
    _matchCounter++;
    match.id = fixture.id || `match-${_matchCounter}`;
    run.matches.push(match);

    // Update group standings
    if (fixture.stage === 'Group Stage' && fixture.group) {
      updateGroupStandings(run, fixture.group, match);
    }
  } catch (error) {
    console.error(`Simulation failed for ${fixture.id}:`, error);
    // Push a placeholder so we don't loop on the same fixture
    run.matches.push({
      id: fixture.id,
      homeTeamId: fixture.homeTeamId,
      awayTeamId: fixture.awayTeamId,
      score: { home: 0, away: 0 },
      stage: fixture.stage,
      group: fixture.group,
      simulated: false,
    });
  }

  // Refresh knockout projections after result
  refreshKnockoutRounds(manifest, run);

  // Find next match
  determineNextMatch(manifest, run);

  const focusMatch = match && (match.homeTeamId === run.selectedTeamId || match.awayTeamId === run.selectedTeamId)
    ? match : null;

  return { run, focusMatch, finalPopup: run.completed, simulated: true };
}

// ============================================================
// Build SimpleTeam for engine
// ============================================================

function buildSimpleTeam(teamData, teamId, tactic) {
  const players = teamData.players.map((p) => ({
    id: p.id,
    name: p.name,
    number: p.number || '',
    currentAbility: p.currentAbility ?? p.rating ?? 130,
    position: p.position,
    stamina: p.stamina ?? 90,
    injured: p.injured ?? false,
  }));

  const startingXi = (teamData.defaultStartingXi && teamData.defaultStartingXi.length === 11)
    ? [...teamData.defaultStartingXi]
    : players.slice(0, 11).map((p) => p.id);

  const bench = players.filter((p) => !startingXi.includes(p.id)).map((p) => p.id);

  return { id: teamId, name: teamData.name || teamId, tactic, players, startingXi, bench };
}

// ============================================================
// Match Result Conversion
// ============================================================

function convertMatchResult(result, fixture, homeData, awayData, homeTactic, awayTactic) {
  const full = result.periods.fullMatch;
  const homeStats = full.teamStats[result.metadata.homeTeamId] || {};
  const awayStats = full.teamStats[result.metadata.awayTeamId] || {};

  let winnerTeamId = null;
  let penaltyWinnerTeamId = null;
  let penaltyShootout = null;

  if (result.score.home > result.score.away) {
    winnerTeamId = result.metadata.homeTeamId;
  } else if (result.score.away > result.score.home) {
    winnerTeamId = result.metadata.awayTeamId;
  } else if (fixture.stage !== 'Group Stage') {
    // Draw in knockout → penalty shootout
    const pr = simulatePenaltyShootout(result.metadata.homeTeamId, result.metadata.awayTeamId, result.seed);
    penaltyWinnerTeamId = pr.winner;
    penaltyShootout = { score: pr.score };
    winnerTeamId = pr.winner;
  }

  const extraTimeScore = null;

  const playerRatings = Object.values(full.playerStats || {}).map((ps) => ({
    playerId: ps.playerId, name: ps.name, teamId: ps.teamId, position: ps.position,
    rating: ps.rating ?? 6.5, minutes: ps.minutes, goals: ps.goals, assists: ps.assists,
    xg: ps.xg, xa: ps.xa, shots: ps.shots, shotsOnTarget: ps.shotsOnTarget,
    passesAttempted: ps.passesAttempted, passesCompleted: ps.passesCompleted,
    keyPasses: ps.keyPasses, crosses: ps.crosses,
    dribblesAttempted: ps.dribblesAttempted, dribblesCompleted: ps.dribblesCompleted,
    tackles: ps.tackles, interceptions: ps.interceptions, clearances: ps.clearances,
    blocks: ps.blocks, saves: ps.saves, foulsCommitted: ps.foulsCommitted,
    foulsWon: ps.foulsWon, yellowCards: ps.yellowCards, redCards: ps.redCards,
    errors: ps.errors, goalsConceded: ps.goalsConceded,
    names: (ps.teamId === homeData?.id ? homeData : awayData)?.players?.find((p) => p.id === ps.playerId)?.names || { en: ps.name },
  }));

  const timeline = (result.timeline || []).map((event) => ({
    minute: event.minute, stoppageTime: event.stoppageTime,
    half: event.half, teamId: event.teamId, playerId: event.playerId,
    relatedPlayerId: event.relatedPlayerId, eventType: event.eventType,
    xg: event.xg, xa: event.xa,
    scoreAfter: event.scoreAfter || { home: 0, away: 0 },
    description: event.description || '',
  }));

  return {
    id: fixture.id || '',
    homeTeamId: result.metadata.homeTeamId,
    awayTeamId: result.metadata.awayTeamId,
    homeTeamName: homeData?.name || result.metadata.homeTeamId,
    awayTeamName: awayData?.name || result.metadata.awayTeamId,
    score: { ...result.score },
    timeline,
    playerRatings,
    homeStats: extractMatchStats(homeStats),
    awayStats: extractMatchStats(awayStats),
    xgHome: homeStats.xg || 0,
    xgAway: awayStats.xg || 0,
    shotsHome: homeStats.shots || 0,
    shotsAway: awayStats.shots || 0,
    possessionHome: homeStats.possession || 50,
    possessionAway: awayStats.possession || 50,
    homeTactic,
    awayTactic,
    stage: fixture.stage || 'Group Stage',
    group: fixture.group || null,
    date: fixture.date || '',
    venue: fixture.venue || '',
    winnerTeamId,
    penaltyWinnerTeamId,
    penaltyShootout,
    extraTimeScore,
    homeLineup: result.teams?.home?.lineup || [],
    awayLineup: result.teams?.away?.lineup || [],
  };
}

function extractMatchStats(s) {
  if (!s) return {};
  return {
    shots: s.shots ?? 0, shotsOnTarget: s.shotsOnTarget ?? 0,
    passesAttempted: s.passesAttempted ?? 0, passesCompleted: s.passesCompleted ?? 0,
    keyPasses: s.keyPasses ?? 0, crosses: s.crosses ?? 0,
    tackles: s.tackles ?? 0, interceptions: s.interceptions ?? 0,
    clearances: s.clearances ?? 0, blocks: s.blocks ?? 0,
    saves: s.saves ?? 0, fouls: s.fouls ?? 0,
    yellowCards: s.yellowCards ?? 0, redCards: s.redCards ?? 0,
    corners: s.corners ?? 0, offsides: s.offsides ?? 0,
  };
}

function simulatePenaltyShootout(homeId, awayId, seed) {
  const rng = new Xoshiro128(seed + 9999);
  let homeScore = 0, awayScore = 0;
  for (let r = 0; r < 5; r++) {
    if (rng.f64() < 0.75) homeScore++;
    if (rng.f64() < 0.75) awayScore++;
  }
  while (homeScore === awayScore) {
    if (rng.f64() < 0.75) homeScore++;
    if (rng.f64() < 0.75) awayScore++;
  }
  return { winner: homeScore > awayScore ? homeId : awayId, score: { home: homeScore, away: awayScore } };
}

// ============================================================
// Group Standings
// ============================================================

function updateGroupStandings(run, groupId, match) {
  const group = run.groups.find((g) => g.id === groupId);
  if (!group) return;

  const home = group.standings.find((s) => s.teamId === match.homeTeamId);
  const away = group.standings.find((s) => s.teamId === match.awayTeamId);
  if (!home || !away) return;

  const hg = match.score.home, ag = match.score.away;
  home.played++; away.played++;
  home.goalsFor += hg; home.goalsAgainst += ag;
  away.goalsFor += ag; away.goalsAgainst += hg;

  if (hg > ag) { home.wins++; home.points += 3; away.losses++; }
  else if (ag > hg) { away.wins++; away.points += 3; home.losses++; }
  else { home.draws++; away.draws++; home.points += 1; away.points += 1; }

  for (const s of [home, away]) {
    s.goalDifference = s.goalsFor - s.goalsAgainst;
  }

  group.standings.sort((a, b) =>
    b.points - a.points ||
    b.goalDifference - a.goalDifference ||
    b.goalsFor - a.goalsFor ||
    a.teamId.localeCompare(b.teamId)
  );
  group.standings.forEach((s, i) => { s.rank = i + 1; });
}

// ============================================================
// Knockout Refresh & Next Match
// ============================================================

function refreshKnockoutRounds(manifest, run) {
  const playedIds = new Set(run.matches.map((m) => m.id));
  const groupStageDone = manifest.fixtures.every((f) => playedIds.has(f.id));

  if (!groupStageDone) return; // Only refresh after group stage is complete

  // Determine qualifiers from standings
  for (const group of run.groups) {
    group.qualifiers = group.standings.slice(0, 2).map((s) => s.teamId);
  }

  // Best 8 third-placed teams across all groups also qualify
  const thirdPlaced = run.groups
    .map((g, idx) => ({ groupIdx: idx, team: g.standings[2] }))
    .filter((t) => t.team)
    .sort((a, b) =>
      b.team.points - a.team.points ||
      b.team.goalDifference - a.team.goalDifference ||
      b.team.goalsFor - a.team.goalsFor
    );
  for (const qt of thirdPlaced.slice(0, 8)) {
    run.groups[qt.groupIdx].qualifiers.push(qt.team.teamId);
  }

  // === FIFA third-placed matchup rules ===
  // 8 group winners face third-placed teams: A, B, D, E, G, I, K, L
  // 4 group winners face runners-up: C, F, H, J
  // Matchups: best third-placed vs worst winner, reverse order, with same-group avoidance
  const THIRD_SLOT_WINNERS = ['A', 'B', 'D', 'E', 'G', 'I', 'K', 'L'];
  const WINNER_TO_SLOT = { A: 79, B: 85, D: 82, E: 75, G: 81, I: 78, K: 88, L: 80 };

  // Rank the 8 qualifying third-placed teams (already computed in thirdPlaced)
  const rankedThirds = thirdPlaced.slice(0, 8).map((t) => ({
    ...t.team,
    groupId: run.groups[t.groupIdx].id,
  }));

  // Rank the 8 group winners that face third-placed teams
  const rankedWinners = THIRD_SLOT_WINNERS
    .map((gId) => {
      const group = run.groups.find((g) => g.id === gId);
      if (!group) return null;
      const winner = group.standings[0];
      return winner ? { ...winner, groupId: gId } : null;
    })
    .filter(Boolean)
    .sort((a, b) =>
      b.points - a.points || b.goalDifference - a.goalDifference || b.goalsFor - a.goalsFor
    );

  // Reverse match: best third vs worst winner, 2nd best vs 2nd worst, etc.
  const matchupPairs = [];
  for (let i = 0; i < 8; i++) {
    matchupPairs.push({
      winnerGroupId: rankedWinners[7 - i].groupId,
      thirdTeamId: rankedThirds[i].teamId,
      thirdGroupId: rankedThirds[i].groupId,
    });
  }

  // Same-group avoidance: swap with next pair if third would face own group winner
  for (let i = 0; i < matchupPairs.length - 1; i++) {
    if (matchupPairs[i].winnerGroupId === matchupPairs[i].thirdGroupId) {
      const tmp = matchupPairs[i + 1].thirdTeamId;
      matchupPairs[i + 1].thirdTeamId = matchupPairs[i].thirdTeamId;
      matchupPairs[i].thirdTeamId = tmp;
      const tmpGroup = matchupPairs[i + 1].thirdGroupId;
      matchupPairs[i + 1].thirdGroupId = matchupPairs[i].thirdGroupId;
      matchupPairs[i].thirdGroupId = tmpGroup;
    }
  }

  // Build slot → third-placed team ID lookup
  const thirdMatchupBySlot = {};
  for (const pair of matchupPairs) {
    thirdMatchupBySlot[WINNER_TO_SLOT[pair.winnerGroupId]] = pair.thirdTeamId;
  }
  run._thirdMatchupBySlot = thirdMatchupBySlot;

  // Regenerate all knockout round matches
  for (const round of run.knockoutRounds) {
    const slots = KNOCKOUT_SLOTS_BY_ROUND.filter((s) => s.stage === round.name);
    const usedTeams = new Set();
    round.matches = slots.map((slot) => {
      const matchId = `match-${slot.id}`;
      const existing = run.matches.find((m) => m.id === matchId);
      if (existing) {
        // Track teams already placed in existing/played matches
        if (existing.homeTeamId && existing.homeTeamId !== 'tbd') usedTeams.add(existing.homeTeamId);
        if (existing.awayTeamId && existing.awayTeamId !== 'tbd') usedTeams.add(existing.awayTeamId);
        return existing;
      }

      const homeTeamId = resolveSlotTeam(run, slot.home, usedTeams, slot.id);
      const awayTeamId = resolveSlotTeam(run, slot.away, usedTeams, slot.id);
      if (homeTeamId) usedTeams.add(homeTeamId);
      if (awayTeamId) usedTeams.add(awayTeamId);

      return {
        id: matchId,
        stage: slot.stage,
        group: null,
        date: slot.date,
        venue: slot.venue,
        homeTeamId: homeTeamId || 'tbd',
        awayTeamId: awayTeamId || 'tbd',
        homeTeamName: homeTeamId || 'TBD',
        awayTeamName: awayTeamId || 'TBD',
        pending: !homeTeamId || !awayTeamId,
      };
    });
  }
}

function resolveSlotTeam(run, source, usedTeams = new Set(), slotId) {
  if (!source || source.length < 2) return null;
  const [kind, ...args] = source;

  if (kind === 'rank') {
    const [groupId, rankIdx] = args;
    const group = run.groups.find((g) => g.id === groupId);
    if (!group) return null;
    const s = group.standings[Number(rankIdx) - 1];
    if (!s || usedTeams.has(s.teamId)) return null;
    return s?.teamId || null;
  }

  if (kind === 'third') {
    // Use pre-computed FIFA matchup rules when available
    if (slotId && run._thirdMatchupBySlot && run._thirdMatchupBySlot[slotId]) {
      const teamId = run._thirdMatchupBySlot[slotId];
      if (!usedTeams.has(teamId)) return teamId;
    }

    // Fallback: try specified groups first
    const groups = args[0] || [];
    const candidates = groups
      .map((gId) => {
        const group = run.groups.find((g) => g.id === gId);
        if (!group) return null;
        const third = group.standings[2];
        return third && group.qualifiers?.includes(third.teamId) ? third : null;
      })
      .filter(Boolean)
      .filter((c) => !usedTeams.has(c.teamId))
      .sort((a, b) => b.points - a.points || b.goalDifference - a.goalDifference || b.goalsFor - a.goalsFor);
    if (candidates.length) return candidates[0].teamId;

    // Fallback: any qualifying third-placed team not yet used
    const fallback = run.groups
      .map((g) => {
        const third = g.standings[2];
        return third && g.qualifiers?.includes(third.teamId) ? third : null;
      })
      .filter(Boolean)
      .filter((c) => !usedTeams.has(c.teamId))
      .sort((a, b) => b.points - a.points || b.goalDifference - a.goalDifference || b.goalsFor - a.goalsFor);
    return fallback[0]?.teamId || null;
  }

  if (kind === 'winner' || kind === 'loser') {
    const matchId = `match-${args[0]}`;
    const match = run.matches.find((m) => m.id === matchId);
    if (!match || !match.winnerTeamId) return null;
    if (kind === 'winner') return match.winnerTeamId;
    return match.winnerTeamId === match.homeTeamId ? match.awayTeamId : match.homeTeamId;
  }

  return null;
}

function determineNextMatch(manifest, run) {
  const playedIds = new Set(run.matches.map((m) => m.id));

  // 1. Check group stage fixtures
  for (const fixture of manifest.fixtures) {
    if (!playedIds.has(fixture.id)) {
      run.nextMatch = fixture;
      run.completed = false;
      return;
    }
  }

  // 2. Check knockout round matches (in order of rounds)
  for (const round of run.knockoutRounds) {
    for (const match of round.matches) {
      if (!playedIds.has(match.id) && match.homeTeamId !== 'tbd' && match.awayTeamId !== 'tbd') {
        run.nextMatch = match;
        run.completed = false;
        return;
      }
    }
  }

  // All matches completed
  run.nextMatch = null;
  run.completed = true;
}
